<template>
  <div class="tester">
    <div>
      <span>{{ errorMessage }}</span>
    </div>
    <div class="d-flex justify-content-start gap-5">
      <PulsFormSelect
        label="Load data from survey"
        :options="surveysOptions"
        @change="onSurveyChange"
      />
      <PulsFormSelect
        label="Load data for group"
        :disabled="!groupOptions.find((g) => g.value === organizationName)"
        :options="groupOptions"
        @change="onGroupChange"
      />
      <PulsFormSelect
        label="Show widget"
        :options="widgetOptions"
        @change="onComponentChange"
      />
    </div>

    <div class="content">
      <!-- JSON Editor Component -->
    </div>
    <div>
      <p>Extra props not in store</p>
      <textarea
        v-model="jsonProps"
        class="form-control"
        rows="10"
        placeholder="Write JSON props here"
      ></textarea>
      <!-- <prism-editor
        v-model="jsonProps"
        class="my-editor"
        :highlight="highlighter"
        line-numbers
      ></prism-editor> -->
      <button class="btn btn-sm btn-primary" @click="updateProps">
        Update
      </button>
    </div>
    <div
      v-if="
        widgetRegistrations[selectedGroup.name] &&
        widgetRegistrations[selectedGroup.name][testWidgetId]
      "
      class="preview"
    >
      <div v-if="widgetError && widgetError.error">
        <ErrorFallbackWidget :id="widgetError.id" :error="widgetError.error" />
      </div>
      <div
        v-else-if="
          !allProps ||
          allProps.loading === undefined ||
          allProps.loading === true
        "
        class="h-100"
      >
        <PulsSkeletonLoader
          :skeletonInfo="
            widgetMap[selectedComponent].skeleton({
              // colData: col.data,
              allProps
            })
          "
        />
      </div>
      <div v-else class="h-100">
        <!-- Dynamic Component Rendering -->
        <component
          :is="widgetMap[selectedComponent].widget"
          v-bind="allProps"
          :id="testWidgetId"
          :testMode="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  makeDataRequest,
  storeDataKeyToRequestTypeMap,
  requestNameToStoreMap
} from '@/stores/data-loader'
import { useActionsStore } from '@/stores/actions-store'
import { useInsightsStore } from '@/stores/insights-store'
import { useResponsesStore } from '@/stores/responses-store'
import { useResultsStore } from '@/stores/results-store'
import { useStatesStore } from '@/stores/states-store'
import { useSurveysStore } from '@/stores/surveys-store'
import { useUserStore } from '@/stores/user-store'
import { useWidgetStore } from '@/stores/widget-store'
// import { mockedSurveyDetails } from './fixtures/survey-details.js'

import AlertWidget, {
  alertWidgetInformation,
  alertWidgetSkeleton
} from '@/components/widgets/alert-widget/AlertWidget.vue'

import CategoriesBarsWidget, {
  categoriesBarsWidgetInformation,
  categoriesBarsWidgetSkeleton
} from '@/components/widgets/categories-bars-widget/CategoriesBarsWidget.vue'

import GroupActionsWidget, {
  groupActionsWidgetInformation,
  groupActionsWidgetSkeleton
} from '@/components/widgets/group-actions-widget/GroupActionsWidget.vue'

import GenericTextWidget, {
  genericTextWidgetInformation,
  genericTextWidgetSkeleton
} from '@/components/widgets/generic-text-widget/GenericTextWidget.vue'

import GroupActionsLocalizableWidget, {
  groupActionsLocalizableWidgetInformation,
  groupActionsLocalizableWidgetSkeleton
} from '@/components/widgets/group-actions-localizable-widget/GroupActionsLocalizableWidget.vue'

import GroupPrioActionsWidget, {
  groupPrioActionsWidgetInformation,
  groupPrioActionsWidgetSkeleton
} from '@/components/widgets/group-prio-actions-widget/GroupPrioActionsWidget.vue'

import InsightsWidget, {
  insightsWidgetInformation,
  insightsWidgetSkeleton
} from '@/components/widgets/insights-widget/InsightsWidget.vue'

import SurveyInfoWidget, {
  surveyInfoWidgetInformation,
  surveyInfoWidgetSkeleton
} from '@/components/widgets/survey-info-widget/SurveyInfoWidget.vue'

import TagValueWidget, {
  tagValueWidgetInformation,
  tagValueWidgetSkeleton
} from '@/components/widgets/tag-value-widget/TagValueWidget.vue'

import TagsDonutsWidget, {
  tagsDonutsWidgetInformation,
  tagsDonutsWidgetSkeleton
} from '@/components/widgets/tags-donuts-widget/TagsDonutsWidget.vue'

import TagsTrendsWidget, {
  tagsTrendsWidgetInformation,
  tagsTrendsWidgetSkeleton
} from '@/components/widgets/tags-trends-widget/TagsTrendsWidget.vue'

import PulsSkeletonLoader from '@/components/shared/PulsSkeletonLoader.vue'
import ErrorFallbackWidget from '@/components/widgets/ErrorFallbackWidget.vue'

import defaultsForWidgets from './fixtures/default-states-for-widgets.json'
// import questionStats from './fixtures/question-stats-chtest-npburzc9.json'

// Prism json editor
// import { PrismEditor } from 'vue-prism-editor'
// import 'vue-prism-editor/dist/prismeditor.min.css'
// import { highlight, languages } from 'prismjs/components/prism-core'
// import 'prismjs/components/prism-clike'
// import 'prismjs/components/prism-javascript'
// import 'prismjs/themes/prism-tomorrow.css' // import syntax highlighting styles

export default {
  name: 'Tester',
  components: {
    // PrismEditor,
    PulsSkeletonLoader,
    ErrorFallbackWidget
  },
  setup() {
    const actionsStore = useActionsStore()
    const insightsStore = useInsightsStore()
    const responsesStore = useResponsesStore()
    const resultsStore = useResultsStore()
    const statesStore = useStatesStore()
    const surveysStore = useSurveysStore()
    const userStore = useUserStore()
    const widgetStore = useWidgetStore()

    const widgetMap = {
      alert_box: {
        widget: AlertWidget,
        information: alertWidgetInformation,
        skeleton: alertWidgetSkeleton
      },
      categories_bars: {
        widget: CategoriesBarsWidget,
        information: categoriesBarsWidgetInformation,
        skeleton: categoriesBarsWidgetSkeleton
      },
      generic_text: {
        widget: GenericTextWidget,
        information: genericTextWidgetInformation,
        skeleton: genericTextWidgetSkeleton
      },
      group_actions: {
        widget: GroupActionsWidget,
        information: groupActionsWidgetInformation,
        skeleton: groupActionsWidgetSkeleton
      },
      group_actions_localizable: {
        widget: GroupActionsLocalizableWidget,
        information: groupActionsLocalizableWidgetInformation,
        skeleton: groupActionsLocalizableWidgetSkeleton
      },
      group_prio_actions: {
        widget: GroupPrioActionsWidget,
        information: groupPrioActionsWidgetInformation,
        skeleton: groupPrioActionsWidgetSkeleton
      },
      insights: {
        widget: InsightsWidget,
        information: insightsWidgetInformation,
        skeleton: insightsWidgetSkeleton
      },
      survey_info: {
        widget: SurveyInfoWidget,
        information: surveyInfoWidgetInformation,
        skeleton: surveyInfoWidgetSkeleton
      },
      tag_value: {
        widget: TagValueWidget,
        information: tagValueWidgetInformation,
        skeleton: tagValueWidgetSkeleton
      },
      tags_donuts: {
        widget: TagsDonutsWidget,
        information: tagsDonutsWidgetInformation,
        skeleton: tagsDonutsWidgetSkeleton
      },
      tags_trends: {
        widget: TagsTrendsWidget,
        information: tagsTrendsWidgetInformation,
        skeleton: tagsTrendsWidgetSkeleton
      }
      // Map other widgets
    }

    // const organizationName = 'chtest'
    // const organizationFullName = 'CH Test'
    // const organizationGroup = {
    //   name: organizationName,
    //   messages: {
    //     en: {
    //       title: organizationFullName
    //     }
    //   }
    // }
    // const organizationName = statesStore.getOrganizationName
    // surveysStore.setSurveyDetails(
    //   mockedSurveyDetails({
    //     organizationName,
    //     surveyName
    //   })
    // )
    // statesStore.setSurveyName(mockedSurveyDetails.name)
    // statesStore.setOrganizationGroup(organizationGroup)
    // statesStore.setSelectedGroup(organizationGroup)
    // statesStore.setOrganizationName(organizationName)

    return {
      actionsStore,
      insightsStore,
      responsesStore,
      resultsStore,
      statesStore,
      surveysStore,
      userStore,
      widgetStore,
      widgetMap
    }
  },
  data() {
    return {
      selectedSurveyName: null,
      widgetError: null,
      jsonProps: 'Write JSON props here',
      errorMessage: '',
      widgetOptions: [
        { value: null, title: 'No Widget' },
        { value: 'alert_box', title: 'Alert Widget' },
        { value: 'categories_bars', title: 'Categories Bars Widget' },
        { value: 'generic_text', title: 'Generic Text Widget' },
        { value: 'group_actions', title: 'Group Actions Widget' },
        {
          value: 'group_actions_localizable',
          title: 'Group Actions Localizable Widget'
        },
        { value: 'group_prio_actions', title: 'Group Prio Actions Widget' },
        { value: 'insights', title: 'Insights Widget' },
        { value: 'survey_info', title: 'Survey Info Widget' },
        { value: 'tag_value', title: 'Tag Value Widget' },
        { value: 'tags_donuts', title: 'Tags Donuts Widget' },
        { value: 'tags_trends', title: 'Tags Trends Widget' }
      ],
      selectedComponent: '',
      defaults: defaultsForWidgets,
      parsedJsonProps: {},
      userIsGroupAdmin: false,
      userIsPulsAdmin: false,
      surveyNameForTest: ''
    }
  },
  computed: {
    surveyDetails() {
      return this.surveysStore.getSurveyDetails || this.mockedSurveyDetails
    },
    organizationName() {
      return this.statesStore.getOrganizationName
    },
    organizationFullName() {
      return this.statesStore.getOrganizationFullName
    },
    selectedGroup() {
      return this.statesStore.selectedGroup
    },
    widgetRegistrations() {
      return this.widgetStore.getWidgetRegistrations
    },
    testWidgetId() {
      return `${this.selectedComponent}-test`
    },
    surveys() {
      return this.surveysStore.getSurveys
    },
    groups() {
      return this.surveysStore.getGroups
    },
    organizationGroup() {
      return this.statesStore.getOrganizationGroup
    },
    userInfo() {
      return this.userStore.getUserInfo
    },
    groupOptions() {
      return [
        {
          value: null,
          title: 'No group'
        },
        {
          value: this.organizationGroup.name,
          title: this.$tr(this.organizationGroup.messages, 'title')
        }
      ].concat(
        this.groups.map((g) => {
          return {
            value: g.name,
            title: this.$tr(g.messages, 'title')
          }
        })
      )
    },
    surveysOptions() {
      return [{ value: null, title: 'No survey' }].concat(
        this.surveys.map((s) => {
          return {
            value: s.name,
            title: `${s.name} - ${s.details.messages.en.title}`
          }
        })
      )
    },
    propsFromStoreForSelectedWidget() {
      const thisWidget =
        this.widgetRegistrations[this.selectedGroup.name][this.testWidgetId]
      if (!thisWidget) {
        return {}
      }
      const props = Object.keys(thisWidget.requests).reduce(
        (acc, requestName) => {
          const reqestData = this.getDataForRequestFromStores(requestName)
          acc.data[requestName] = reqestData.data
          acc.loading[requestName] = reqestData.loading
          acc.error[requestName] = reqestData.error
          return acc
        },
        { data: {}, loading: {}, error: {} }
      )
      const widgetHasLoadingProp = Object.keys(props.loading).some(
        (key) => props.loading[key] !== false
      )
      const widgetHasLoadingError = Object.keys(props.error).some(
        (key) => props.error[key]
      )
      props.data.organizationName = this.organizationName
      props.data.selectedGroupName = this.selectedGroup.name
      props.data.selectedGroupTitle = this.$tr(
        this.selectedGroup.messages,
        'title'
      )
      props.data.organizationFullName = this.organizationFullName
      props.data.userIsGroupAdmin = this.userIsGroupAdmin
      props.data.userIsPulsAdmin = this.userIsPulsAdmin
      // props.data.fullScreen = fullScreen
      props.data.userInfo = this.userInfo
      props.data.surveyName = this.selectedSurveyName
      return {
        ...props.data,
        // ...colData,
        loading: widgetHasLoadingProp,
        error: widgetHasLoadingError
      }
    },
    allProps() {
      return {
        updateWidgetSliderState: () => {},
        ...this.parsedJsonProps,
        ...this.propsFromStoreForSelectedWidget
      }
    }
  },
  watch: {
    // To autoload survey, group and select widget
    surveys() {
      // this.onSurveyChange({ target: { value: '2410189YtWtHZD' } })
    },
    groups() {
      // this.onGroupChange({ target: { value: '6nT6YvwG' } })
      // this.onComponentChange({ target: { value: 'categories_bars' } })
    },
    widgetRegistrations: {
      handler() {
        const requestedData = {}
        Object.keys(
          this.widgetRegistrations[this.selectedGroup.name] || {}
        ).forEach((widgetName) => {
          const widget =
            this.widgetRegistrations[this.selectedGroup.name][widgetName]
          if (widget && widget.requests) {
            Object.keys(widget.requests).forEach((requestName) => {
              const dataName = storeDataKeyToRequestTypeMap[requestName]
              requestedData[dataName] = {
                data: widget.data,
                testMode: widget.testMode
              }
            })
          }
        })
        Object.keys(requestedData).forEach((dataName) => {
          makeDataRequest({
            dataName,
            widgetData: requestedData[dataName].data
          })
        })
      },
      deep: true
    }
    // props(newProps) {
    //   try {
    //     this.parsedJsonProps = newProps ? JSON.parse(newProps) : {}
    //     this.errorMessage = ''
    //   } catch (e) {
    //     this.errorMessage = 'Invalid JSON format.'
    //   }
    // }
  },
  errorCaptured(err, vm, info) {
    // Handle the error and set hasError to true
    this.widgetError = {
      error: err,
      id: vm.id,
      info: info
    }
    // Optionally log the error or perform other actions
    // Returning false stops the error from propagating further
    return false
  },
  mounted() {
    this.surveysStore.loadSurveysAction({})
    // const organizationName = 'chtest'
    // const organizationFullName = 'CH Test'
    // this.statesStore.setOrganizationName(organizationName)
    // const organizationGroup = {
    //   name: organizationName,
    //   messages: {
    //     en: {
    //       title: organizationFullName
    //     }
    //   }
    // }
    // this.statesStore.setOrganizationGroup(organizationGroup)
    // this.statesStore.setSelectedGroup(organizationGroup)
  },
  methods: {
    updateProps() {
      try {
        this.parsedJsonProps = this.jsonProps ? JSON.parse(this.jsonProps) : {}
        this.errorMessage = ''
      } catch (e) {
        this.errorMessage = 'Invalid JSON format.'
      }
    },
    getDataForRequestFromStores(requestName) {
      const storeName = requestNameToStoreMap[requestName]
      switch (storeName) {
        case 'surveysStore':
          return {
            data: this.surveysStore[requestName],
            loading: this.surveysStore.loading[requestName],
            error: this.surveysStore.error[requestName]
          }
        case 'resultsStore':
          return {
            data: this.resultsStore[requestName],
            loading: this.resultsStore.loading[requestName],
            error: this.resultsStore.error[requestName]
          }
        case 'insightsStore':
          return {
            data: this.insightsStore[requestName],
            loading: this.insightsStore.loading[requestName],
            error: this.insightsStore.error[requestName]
          }
        case 'actionsStore':
          return {
            data: this.actionsStore[requestName],
            loading: this.actionsStore.loading[requestName],
            error: this.actionsStore.error[requestName]
          }
        default:
          console.error('Unknown store for request', storeName, requestName)
          break
      }
    },
    highlighter(code) {
      return highlight(code, languages.js) // languages.<insert language> to return html with markup
    },
    onSurveyChange(event) {
      this.resultsStore.$reset()
      this.widgetStore.$reset()
      const surveyName = event.target.value
      this.selectedSurveyName = surveyName
      this.statesStore.setSurveyName(surveyName)
      this.loadSurveyDetails(surveyName)
    },
    loadSurveyDetails(surveyName) {
      this.surveysStore.loadSurveyDetailsAction({ surveyName })
    },
    onGroupChange(event) {
      const groupName = event.target.value
      const group =
        groupName === this.organizationGroup.name
          ? this.organizationGroup
          : this.groups.find((g) => g.name === groupName)
      // set selected group in states store
      this.statesStore.setSelectedGroup(group)
    },
    onComponentChange(event) {
      const componentName = event.target.value
      this.loadDefaultStateForComponent(componentName)
      this.registerWidget({
        id: `${componentName}-test`,
        information: this.widgetMap[componentName].information({
          colData: defaultsForWidgets[componentName].data
        }),
        data: defaultsForWidgets[componentName].data
      })
    },
    registerWidget({ id, information, data }) {
      this.widgetStore.registerWidgetAction({
        groupName: this.selectedGroup.name,
        id,
        information,
        data
      })
    },
    loadDefaultStateForComponent(componentName) {
      try {
        this.jsonProps = JSON.stringify(
          defaultsForWidgets[componentName],
          null,
          2
        )
        this.updateProps()
      } catch (error) {
      } finally {
        this.selectedComponent = componentName
      }
    }
  }
}
</script>

<style>
.content {
  display: flex;
}
/* required class */
.my-editor {
  /* we dont use `language-` classes anymore so thats why we need to add background and text color manually */
  background: #2d2d2d;
  color: #ccc;
  max-height: 500px;
  /* you must provide font-family font-size line-height. Example: */
  font-family:
    Fira code,
    Fira Mono,
    Consolas,
    Menlo,
    Courier,
    monospace;
  font-size: 14px;
  line-height: 1.5;
  padding: 5px;
}

/* optional class for removing the outline */
.prism-editor__textarea:focus {
  outline: none;
}
</style>
